/** Diet vocabulary shared by the backend, the nutrition engine and every app. */

export enum MealSlot {
  BREAKFAST = "breakfast",
  SECOND_BREAKFAST = "second_breakfast",
  LUNCH = "lunch",
  SNACK = "snack",
  DINNER = "dinner",
  SUPPER = "supper",
}

export enum DietGoal {
  LOSE = "lose",
  MAINTAIN = "maintain",
  GAIN = "gain",
}

/** Daily activity outside training. Maps to the TDEE multiplier in the nutrition engine. */
export enum ActivityLevel {
  SEDENTARY = "sedentary",
  LIGHT = "light",
  MODERATE = "moderate",
  HIGH = "high",
  VERY_HIGH = "very_high",
}

export enum DietType {
  STANDARD = "standard",
  VEGETARIAN = "vegetarian",
  VEGAN = "vegan",
  PESCATARIAN = "pescatarian",
  HIGH_PROTEIN = "high_protein",
  LOW_CARB = "low_carb",
  KETO = "keto",
  GLUTEN_FREE = "gluten_free",
  LACTOSE_FREE = "lactose_free",
}

/** The 14 allergens EU law requires on labels (Regulation 1169/2011, Annex II). */
export enum Allergen {
  GLUTEN = "gluten",
  CRUSTACEANS = "crustaceans",
  EGGS = "eggs",
  FISH = "fish",
  PEANUTS = "peanuts",
  SOY = "soy",
  MILK = "milk",
  NUTS = "nuts",
  CELERY = "celery",
  MUSTARD = "mustard",
  SESAME = "sesame",
  SULPHITES = "sulphites",
  LUPIN = "lupin",
  MOLLUSCS = "molluscs",
}

/** Shops a diet's ingredients should be easy to find in. Polish chains first. */
export enum Shop {
  BIEDRONKA = "biedronka",
  LIDL = "lidl",
  KAUFLAND = "kaufland",
  AUCHAN = "auchan",
  CARREFOUR = "carrefour",
  DINO = "dino",
  NETTO = "netto",
  ZABKA = "zabka",
  ALDI = "aldi",
  STOKROTKA = "stokrotka",
  LEWIATAN = "lewiatan",
  ONLINE = "online",
}

export enum KitchenEquipment {
  OVEN = "oven",
  MICROWAVE = "microwave",
  BLENDER = "blender",
  AIR_FRYER = "air_fryer",
  GRILL_PAN = "grill_pan",
  SLOW_COOKER = "slow_cooker",
  LUNCHBOX = "lunchbox",
}

/** Aisles a shopping list is grouped by. */
export enum FoodCategory {
  VEGETABLES = "vegetables",
  FRUIT = "fruit",
  MEAT = "meat",
  FISH = "fish",
  DAIRY = "dairy",
  EGGS = "eggs",
  BREAD = "bread",
  GRAINS = "grains",
  LEGUMES = "legumes",
  NUTS_SEEDS = "nuts_seeds",
  OILS_FATS = "oils_fats",
  SPICES = "spices",
  FROZEN = "frozen",
  DRINKS = "drinks",
  SWEETS = "sweets",
  OTHER = "other",
}

export enum FoodSource {
  OPEN_FOOD_FACTS = "off",
  USDA = "usda",
  CUSTOM = "custom",
}

/** How a meal got into the log. Photo, label and text go through a meal scan (ADR 0012). */
export enum MealLogSource {
  PHOTO = "photo",
  LABEL = "label",
  TEXT = "text",
  BARCODE = "barcode",
  SEARCH = "search",
  PLAN = "plan",
  RECIPE = "recipe",
}

/**
 * What the athlete says about a whole day. A fast is real: little or nothing eaten counts as eaten.
 * An incomplete day was not all logged, so it stays out of averages, expenditure and the check-in.
 */
export enum NutritionDayState {
  FASTING = "fasting",
  INCOMPLETE = "incomplete",
}

export enum MealLogStatus {
  ANALYZING = "analyzing",
  READY = "ready",
  FAILED = "failed",
}

/** Why a meal scan gave nothing, so the app can say what to try instead. */
export enum MealScanFailure {
  /** No food (or no readable label) in the photo or the words. */
  NO_FOOD = "no_food",
  /** The assistant declined. */
  DECLINED = "declined",
  /** The assistant could not be reached after every retry. */
  UNAVAILABLE = "unavailable",
}

/** Where a nutrition target came from. */
export enum NutritionTargetSource {
  /** The engine's starting estimate from body details and the diet questionnaire. */
  ESTIMATE = "estimate",
  /** A check-in the athlete accepted. */
  CHECK_IN = "check_in",
  /** Typed in by the athlete. */
  MANUAL = "manual",
}

/** What happened to a check-in's proposal. */
export enum CheckInStatus {
  ACCEPTED = "accepted",
  KEPT = "kept",
  /** Waiting for the trainer whose diet the athlete follows. */
  PENDING_TRAINER = "pending_trainer",
  /** The trainer kept their diet's target. */
  DECLINED_BY_TRAINER = "declined_by_trainer",
}
